import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../lib/axios";
import {
  Rocket, Users, GitBranch, Mail, Clock, CheckCircle2, XCircle, Info,
  Pause, RotateCcw, BarChart3, RefreshCw, AlertTriangle,
} from "lucide-react";
import "./LaunchHeroCard.css";

export default function LaunchHeroCard({ campaignId, campaignStatus, formData, onLaunch, onPause, onResume, launching, pauseReason }) {
  const navigate = useNavigate();
  const [leadCount, setLeadCount] = useState(0);
  const [stepCount, setStepCount] = useState(0);
  const [scheduleSet, setScheduleSet] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [leadsRes, seqRes, schedRes] = await Promise.all([
          axios.get(`contacts/get-leads/${campaignId}`).catch(() => ({ data: [] })),
          axios.get(`email-campaign/get-sequence/${campaignId}`).catch(() => ({ data: {} })),
          axios.get(`email-campaign/get-schedule/${campaignId}`).catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        const leads = Array.isArray(leadsRes.data) ? leadsRes.data
          : Array.isArray(leadsRes.data?.leads) ? leadsRes.data.leads
          : Array.isArray(leadsRes.data?.data) ? leadsRes.data.data
          : [];
        setLeadCount(leads.length);
        setStepCount(seqRes.data?.steps?.length || 0);
        setScheduleSet(!!schedRes.data?.schedule_name);
      } catch (err) {
        console.warn("Launch hero data fetch failed:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [campaignId]);

  const accountCount = formData.accountIds?.length || 0;
  const emailsPerDay = formData.emailsPerDay || 50;
  const estDays = leadCount > 0 ? Math.ceil(leadCount / emailsPerDay) : 0;

  const checks = [
    { key: "leads", label: `Leads loaded (${leadCount})`, ok: leadCount > 0, fix: "leads" },
    { key: "steps", label: `Sequence configured (${stepCount} step${stepCount !== 1 ? "s" : ""})`, ok: stepCount > 0, fix: "sequence" },
    { key: "schedule", label: "Schedule set", ok: scheduleSet, fix: "schedule" },
    { key: "accounts", label: `Email accounts selected (${accountCount})`, ok: accountCount > 0, fix: null },
  ];
  const allReady = checks.every((c) => c.ok);
  const failing = checks.filter((c) => !c.ok).length;

  if (loading) {
    return (
      <div className="launch-hero launch-hero-loading">
        <RefreshCw size={24} className="animate-spin" />
      </div>
    );
  }

  if (campaignStatus === "completed") {
    return (
      <div className="launch-hero launch-hero-completed">
        <div className="launch-hero-icon"><CheckCircle2 size={44} color="#fff" /></div>
        <h3>Campaign Complete</h3>
        <p>Your campaign finished. Check analytics for results.</p>
        <button className="launch-hero-btn" onClick={() => navigate(`/campaigns/analytics/${campaignId}`)}>
          <BarChart3 size={16} /> View Analytics
        </button>
      </div>
    );
  }

  if (campaignStatus === "paused") {
    return (
      <div className="launch-hero launch-hero-paused">
        <div className="launch-hero-icon"><Pause size={44} color="#fff" /></div>
        <h3>Campaign Paused</h3>
        {pauseReason && (
          <div className="launch-hero-pause-banner">
            <AlertTriangle size={14} /> {pauseReason}
          </div>
        )}
        <p>Resume when ready — sends will pick up where they left off.</p>
        <button className="launch-hero-btn" onClick={onResume} disabled={launching || !!pauseReason}>
          {launching ? <RefreshCw size={16} className="animate-spin" /> : <RotateCcw size={16} />}
          Resume Campaign
        </button>
      </div>
    );
  }

  if (campaignStatus === "active") {
    return (
      <div className="launch-hero launch-hero-active">
        <div className="launch-hero-icon"><span className="pulse-dot" /></div>
        <h3>Campaign Running</h3>
        <div className="launch-hero-stats">
          <Stat icon={Users} value={leadCount} label="Leads" />
          <Stat icon={GitBranch} value={stepCount} label={`Step${stepCount !== 1 ? "s" : ""}`} />
          <Stat icon={Mail} value={accountCount} label={`Account${accountCount !== 1 ? "s" : ""}`} />
          <Stat icon={Clock} value={`${emailsPerDay}/day`} label="Send rate" />
        </div>
        <button className="launch-hero-btn" onClick={onPause} disabled={launching}>
          {launching ? <RefreshCw size={16} className="animate-spin" /> : <Pause size={16} />}
          Pause Campaign
        </button>
      </div>
    );
  }

  return (
    <div className="launch-hero">
      <div className="launch-hero-icon launch-hero-rocket"><Rocket size={44} color="#fff" /></div>
      <h3>Ready to Launch?</h3>
      <p>Final pre-flight check</p>

      <div className="launch-hero-stats">
        <Stat icon={Users} value={leadCount} label="Leads" />
        <Stat icon={GitBranch} value={stepCount} label={`Step${stepCount !== 1 ? "s" : ""}`} />
        <Stat icon={Mail} value={accountCount} label={`Account${accountCount !== 1 ? "s" : ""}`} />
        <Stat icon={Clock} value={estDays ? `~${estDays} d` : "—"} label="Est. time" />
      </div>

      <div className="launch-hero-checklist">
        <div className="launch-hero-checklist-title">Pre-flight checklist</div>
        {checks.map((c) => (
          <div key={c.key} className={`launch-hero-check ${c.ok ? "ok" : "fail"}`}>
            {c.ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
            <span className="launch-hero-check-label">{c.label}</span>
            {!c.ok && c.fix && (
              <button className="launch-hero-fix" onClick={() => navigate(`/campaigns/${c.fix}/${campaignId}`)}>
                Fix →
              </button>
            )}
          </div>
        ))}
        <div className="launch-hero-check info">
          <Info size={16} />
          <span className="launch-hero-check-label">Tracking settings reviewed (advisory)</span>
        </div>
      </div>

      <button className="launch-hero-btn big" onClick={onLaunch} disabled={launching || !allReady}>
        {launching ? <RefreshCw size={16} className="animate-spin" /> : <Rocket size={16} />}
        Launch Campaign Now
      </button>
      <div className={`launch-hero-helper ${allReady ? "" : "warn"}`}>
        {allReady
          ? `All systems go · ~${estDays} day${estDays !== 1 ? "s" : ""} to complete at ${emailsPerDay}/day`
          : `Resolve ${failing} item${failing !== 1 ? "s" : ""} before launching`}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="launch-hero-stat">
      <Icon size={18} />
      <div className="launch-hero-stat-value">{value}</div>
      <div className="launch-hero-stat-label">{label}</div>
    </div>
  );
}
